import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../models/user';
import { StoredUserInfo } from '../models/storeduserinfo';
import { UserService } from './user';
import Stream from 'node:stream';

// Shared Error Response

export interface ApiErrorResponse {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  correlationId: string;
  errorCode: string;
  timestamp: string;
  errors: Record<string, string>;
  stackTrace: string;
  innerException: {
    message: string;
    type: string;
    stackTrace: string;
  };
}

export interface ServiceRequestDocumentDetails {
  file: Stream;
  description: string;
}

export interface CreateServiceRequestRequest {
  userId: number;
  userName: string;
  email: string;
  phoneNumber: string;
  caseNumber: string;
  category: string;
  formType: string;
  formTypeOtherText: string;
  caseType: string;
  subCaseType: string;
  description: string;
  preferredContact: string;
  preferredLanguage: string;
  zipCode: string;
  status: string;
  createdBy: number;
  serviceRequestDocuments: ServiceRequestDocumentDetails[];
}

export interface CreateServiceRequestResponse {
  serviceRequestId: number;
  serviceRequestNo: string;
}

//  Get Service Request Details
export interface ServiceRequestDetail {
  serviceRequestId: number;
  userId: number;
  caseNumber: string | null;
  category: string;
  formType: string | null;
  formTypeOtherText: string | null;
  caseType: string | null;
  subCaseType: string | null;
  description: string;
  preferredContact: string | null;
  preferredLanguage: string | null;
  visitedBefore: string | null;
  zipCode: string | null;
  status: string;
  submittedDate: string;
  closedDate: string | null;
  team: string | null;
  assignedToUserId: number | null;
  createdDate: string;
  createdBy: number;
  modifiedDate: string | null;
  modifiedBy: number | null;
}

export interface ServiceRequestDocument {
  serviceRequestDocumentId: number;
  serviceRequestId: number;
  serviceRequestConversationId: number | null;
  uploadedByUserType: string;
  originalFileName: string;
  fileExtension: string;
  fileSizeBytes: number;
  fileUrl: string;
  filePath: string;
  description: string;
  createdDate: string;
  createdBy: number;
}

export interface ServiceRequestDetailsResponse {
  serviceRequest: ServiceRequestDetail;
  serviceRequestDocuments: ServiceRequestDocument[];
}

// Get User Service Requests

export interface UserServiceRequestCategoryDetail {
  serviceRequestCategoryId: number;
  categoryId: number;
  formTypeId: number;
  otherFormText: string;
}

export interface UserServiceRequestSummary {
    serviceRequestId: number;
    serviceRequestNo: string;
  caseNumber: string | null;
  category: string;
  caseType: string;
  description: string;
  status: string;
  submittedDate: string;
  modifiedDate: string | null;
  serviceRequestCategoryDetails: UserServiceRequestCategoryDetail[];
}
export type ConversationMessageType = 'Public Reply' | 'Staff Reply' | 'Staff Only';
// Service Request Conversation (replaces History)
export interface ServiceRequestConversation {
  serviceRequestConversationId: number;
  serviceRequestId: number;
  messageType: ConversationMessageType;
  messageBody: string;
  senderUserId: number;
  isRead: boolean;
  readDate: string | null;
  readByUserId: number | null;
  hasAttachments: boolean;
  isNotificationSent: boolean;
  notificationSentDate: string | null;
  notificationChannel: string | null;
  createdDate: string;
  createdBy: number;
  modifiedDate: string | null;
  modifiedBy: number | null;
    // UI only - not from API
  isSending?: boolean;
}

export interface ConversationDocumentDetails {
  file: File;
  description: string;
}


export interface CreateConversationMessageRequest {
  serviceRequestId: number;
  messageType: ConversationMessageType;
  messageBody: string;
  senderUserId: number;
  hasAttachments: boolean;
  createdBy: number;
  conversationDocuments?: ConversationDocumentDetails[] | null;
}

@Injectable({
  providedIn: 'root',
})
export class ServiceRequestService {
  private http = inject(HttpClient);
  private userService = inject(UserService);
  private baseUrl = `${environment.dashApiUrl}/api/ServiceRequest`;

  createServiceRequest(payload: FormData): Observable<CreateServiceRequestResponse> {
    return this.http.post<CreateServiceRequestResponse>(
      `${this.baseUrl}/create`,
      payload
    );
  }

  getServiceRequestDetails(serviceRequestId: number): Observable<ServiceRequestDetailsResponse> {
    const params = new HttpParams().set('serviceRequestId', serviceRequestId);
    return this.http.get<ServiceRequestDetailsResponse>(
      `${this.baseUrl}/getServiceRequestDetails`,
      { params }
    );
  }

  getUserServiceRequests(): Observable<UserServiceRequestSummary[]> {
    const user = this.userService.getUserInfoFromStorage();
    const params = new HttpParams().set('userId', user?.userId ?? 0);
    return this.http.get<UserServiceRequestSummary[]>(
      `${this.baseUrl}/getUserServiceRequestDetails`,
      { params }
    );
  }

  //TODO: update request to use new endpoint or updated params for the api call to exclude "StaffOnly" replies
  getServiceRequestConversation(serviceRequestId: number): Observable<ServiceRequestConversation[]> {
    //filter the collection to only include publicreply and staffreply messages
    return this.http.get<ServiceRequestConversation[]>(
      `${environment.dashApiUrl}/api/ServiceRequestConversation/serviceRequest/${serviceRequestId}`
    ).pipe(map(messages => messages.filter(m => m.messageType === 'Public Reply' || m.messageType === 'Staff Reply')));
  }

  createConversationMessage(payload: CreateConversationMessageRequest): Observable<ServiceRequestConversation> {
        const formData = new FormData();
    formData.append('serviceRequestId', payload.serviceRequestId.toString());
    formData.append('messageType', payload.messageType);
    formData.append('messageBody', payload.messageBody);
    formData.append('senderUserId', payload.senderUserId.toString());
    formData.append('hasAttachments', payload.hasAttachments.toString());
    formData.append('createdBy', payload.createdBy.toString());
    if (payload.conversationDocuments?.length) {
      payload.conversationDocuments.forEach((doc, index) => {
        formData.append(`ConversationDocuments[${index}].File`, doc.file, doc.file.name);
        formData.append(`ConversationDocuments[${index}].Description`, doc.description);
      });
    }
    
    return this.http.post<ServiceRequestConversation>(
      `${environment.dashApiUrl}/api/ServiceRequestConversation/create`,
      formData
    );
  }
}
